/* --- Dependencies --- */
import React, { Component } from "react";
import { connect } from "react-redux";
import { injectIntl } from "react-intl";
/* --- React Components --- */
import Icon from "../../components/presentational/Icon";
/* --- Redux Actions --- */
import { resetMessages } from "../../domain/misc/miscActions";
/* --- Constants --- */
import * as StatusMessages from "../../constants/StatusMessages";
class StatusMessage extends Component {
    /* ----- React Lifecycle Methods ------ */
    /**
     * React lifecycle method. Fires once when the component is added to the DOM.
     *
     * @method componentDidMount
     * @private
     */
    componentDidMount() {
        /* Reset messages */
        if (this.props.autoReset && !this.props.willPersist && !this.props.isLoad) {
            this.props.resetMessages(this.props.domain);
        }
    }
    /**
     * Render lifecycle method
     *
     * @method render
     * @private
     * @returns {HTMLElement} Component element
     */
    render() {
        let visuallyHiddenClass = "";
        let hiddenClass = "";
        let messageClass = "text";
        let message = null;
        if (!this.props.isActive) {
            visuallyHiddenClass = "u-isVisuallyHidden";
            hiddenClass = "u-isHidden";
        }
        if (this.props.isActive) {
            if (this.props.isLoad) {
                message = this._formatMessage(StatusMessages.LOADING.GENERAL);
            }
            else {
                message = [
                    this.props.customMessage,
                    this._buildMessageFromError()
                ].map(it => this._formatMessage(it)).join(" ");
            }
        }
        if (this.props.isError) {
            messageClass = "error";
        }
        else if (this.props.isSuccess) {
            messageClass = "confirmation";
        }
        if (!this.props.isLoad) {
            return (<div className={`vr vr_x5 ${visuallyHiddenClass} StatusMessage`} aria-live="assertive" role="alert">
					<span className={messageClass}>
						{message}
					</span>
				</div>);
        }
        else {
            return (<div className={`vr vr_x5 ${visuallyHiddenClass} StatusMessage StatusMessage--loading`} aria-live="assertive" role="alert">
					<div className="loading">
						<div className={`loading-icon ${hiddenClass}`}>
							<Icon icon="loading"/>
						</div>
						<div className="loading-label">
							{message}
						</div>
					</div>
				</div>);
        }
    }
    /**
     * React lifecycle method. Fires once before the component is removed from the DOM.
     *
     * @method componentWillUnmount
     * @private
     */
    componentWillUnmount() {
        /* Reset persistant messages */
        if (this.props.autoReset && this.props.willPersist && !this.props.isLoad) {
            this.props.resetMessages(this.props.domain);
        }
    }
    /* ----- Helper Methods ----- */
    /**
     * Builds message from related error
     *
     * @method _buildMessageFromError
     * @returns {String} Error message to display
     * @private
     */
    _buildMessageFromError() {
        if (this.props.error) {
            switch (this.props.domain) {
                case "auth":
                    return this._authMessage();
                case "validation":
                    return this._validationMessage();
                case "contact":
                    return this._contactMessage();
                case "accts":
                    return this._acctsMessage();
                case "profile":
                    return this._profileMessage();
                case "payment":
                    return this._paymentMessage();
                default:
                    return this.props.error.message || "";
            }
        }
        else {
            return "";
        }
    }
    /**
     * Builds accounts message
     *
     * @method _acctsMessage
     * @returns {String} Error message to display
     * @private
     */
    _acctsMessage() {
        switch (this.props.error.response.status) {
            case 401:
                return StatusMessages.ACCTS.INVALID_CRED_401;
            default:
                return this.props.error.message ? this.props.error.message : "";
        }
    }
    /**
     * Builds profile message
     *
     * @method _profileMessage
     * @returns {String} Error message to display
     * @private
     */
    _profileMessage() {
        return this.props.error.message ? this.props.error.message : "";
    }
    /**
     * Builds authentication message
     *
     * @method _authMessage
     * @returns {String} Error message to display
     * @private
     */
    _authMessage() {
        switch (this.props.error.response.status) {
            case 401:
                return StatusMessages.AUTH.INVALID_CRED_401;
            case 403:
                return StatusMessages.AUTH.INVALID_CRED_403;
            case 409:
                return StatusMessages.AUTH.ACCOUNT_LOCKED_409;
            case 410:
                return StatusMessages.AUTH.FORGOT_PASSWORD_BAD_URL_410;
            case 417:
                return StatusMessages.AUTH.CAPTCHA_INVALID_417;
            case 429:
                return StatusMessages.AUTH.CAPTCHA_REQUIRED_429;
            default:
                return this.props.error.message ? this.props.error.message : "";
        }
    }
    /**
     * Builds validation message
     *
     * @method _validationMessage
     * @returns {String} Error message to display
     * @private
     */
    _validationMessage() {
        const { status } = (this.props.error.response || {});
        switch (status) {
            case 404:
                return StatusMessages.VALIDATION.ACCOUNT_MISSING_404;
            case 417:
                return StatusMessages.VALIDATION.CAPTCHA_INVALID_417;
            case 429:
                return StatusMessages.VALIDATION.CAPTCHA_REQUIRED_429;
            case 400:
                return StatusMessages.VALIDATION.INCOMPLETE_400;
            case 401:
                return StatusMessages.VALIDATION.LOGGED_IN_401;
            case 412:
                return StatusMessages.VALIDATION.EMAIL_IN_USE_412;
            case 451:
                return StatusMessages.VALIDATION.ACCOUNT_IN_2REG_451;
            case 460:
                return StatusMessages.VALIDATION.USER_LIMIT_EXCEEDED_460;
            case 461:
                return StatusMessages.VALIDATION.ACCOUNT_CLOSED_461;
            case 502:
                return StatusMessages.VALIDATION.BAD_DATA_502;
            default:
                return this.props.error.message ? this.props.error.message : "";
        }
    }
    /**
     * Builds contact message
     *
     * @method _contactMessage
     * @returns {String} Error message to display
     * @private
     */
    _contactMessage() {
        switch (this.props.error.response.status) {
            case 417:
                return StatusMessages.CONTACT.CAPTCHA_INVALID_417;
            case 429:
                return StatusMessages.CONTACT.CAPTCHA_REQUIRED_429;
            default:
                return this.props.error.message ? this.props.error.message : "";
        }
    }
    _paymentMessage() {
        switch (this.props.error.response.status) {
            case 404:
                return StatusMessages.PAYMENT.PAYMENT_NOT_FOUND;
            default:
                return this.props.error.message ? this.props.error.message : "Unknown";
        }
    }
    _formatMessage(message) {
        if (message == null) {
            return "";
        }
        if (typeof message === "string") {
            return message;
        }
        return this.props.intl.formatMessage(message);
    }
}
/* React default properties */
StatusMessage.defaultProps = {
    customMessage: "",
    domain: null,
    error: null,
    isError: false,
    isLoad: false,
    isSuccess: false,
    willPersist: false,
    autoReset: true
};
const bindActionsToProps = {
    resetMessages
};
/* Create and export container component that wraps StatusMessage for property binding */
export default connect(null, bindActionsToProps)(injectIntl(StatusMessage));



// WEBPACK FOOTER //
// ./src/assets/scripts/components/presentational/StatusMessage.tsx